import type {
  ApolloError,
  DocumentNode,
  OperationVariables,
  QueryHookOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { useQuery } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import * as Sentry from '@sentry/react';

/**
 * Wraps Apollo's `useQuery` for calls to the gateway GraphQL client, tagging every reported
 * error with the operation name and HTTP status code. Without this, every gatewayGql failure
 * gets Sentry's generic "unhandled promise rejection" message with no operation context, so
 * distinct failing queries all collapse into one opaque bucket (see Sentry issues
 * ADMIN-DASHBOARD-292 and ADMIN-DASHBOARD-2TZ).
 *
 * This only reports the error — it does not hide it. Callers must still check `error` before
 * rendering `data`, same as with `useQuery` directly.
 */
export const useGatewayGqlQuery = <TData = unknown, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: QueryHookOptions<TData, TVariables>
) => {
  const operationName = getOperationName(query) ?? 'unknown';

  return useQuery<TData, TVariables>(query, {
    ...options,
    context: { ...options?.context, clientName: 'gatewayGql' },
    onError: (error: ApolloError) => {
      // Network-layer failures are already reported by catchLink (apollo-link-naptime), which
      // sits in the gatewayGql link chain too -- capturing them again here would double-report
      // the same error to Sentry. Only report GraphQL-level errors (no networkError) from here.
      if (!error.networkError) {
        Sentry.captureException(error, {
          tags: { operationName, httpStatusCode: 'unknown' },
          extra: { operationName, variables: options?.variables },
          fingerprint: ['gateway-gql-query-error', operationName, 'unknown'],
        });
      }
      options?.onError?.(error);
    },
  });
};

export default useGatewayGqlQuery;
